import m, {MithrilComponent} from '@bitstillery/mithril'
import {Badge, Button, Dropdown, Icon, Link, ThemeSwitcher} from '@bitstillery/common/components/portal'
import {classes} from '@bitstillery/common/lib/utils'
import {$t} from '@bitstillery/common/app'
import {get_route} from '@bitstillery/common/lib/utils'
import {HeroBanner} from '@/cms/components/shell/hero/hero'
import {apply_document_meta, type PageCopyKind} from '@/cms/lib/page_meta'
import {PAGE_NOT_FOUND} from '@/cms/lib/not_found'
import {$m, $s} from '@/app'
import {BarLanguageSwitcher} from '@/components/bar_language_switcher'
import {cart_bounce} from '@/lib/cart_bounce'
import {PricelistDownload} from '@/components/pricelist_download/pricelist_download'
import {SearchSite} from '@/components/search_site/search_site'
import {Breadcrumbs} from '@/components/breadcrumbs/breadcrumbs'

class HeaderInner extends MithrilComponent {
    view() {
        const route = m.route.get()
        const is_cms_layout = !route.startsWith('/shop')

        const shop_route = get_route('/shop')
        const checkout_route = get_route('/shop/checkout')
        const favourites_route = get_route('/shop/favourites')

        const go_to_offers = (path: string) => {
            void m.route.set($s.identity.token ? path : `/login?redirect=${encodeURIComponent(path)}`)
        }

        // Toggle the mobile drawer (shared by the authenticated and unauthenticated
        // hamburger). Opening it collapses the other bottom sheets so only one is up.
        const toggle_menu = () => {
            if (!$s.panels.view.menu.collapsed) {
                $s.panels.view.menu.collapsed = true
                return
            }
            $s.panels.view.menu.collapsed = false
            $s.panels.view.filters.collapsed = true
            $s.panels.view.categories.collapsed = true
            $s.panels.context.collapsed = true
        }

        // Always in the DOM; CSS (.portal-shop-menu-trigger) reveals it only on mobile,
        // preserving SSR/browser parity across breakpoints.
        const menu_trigger = (
            <Icon className='btn-action portal-shop-menu-trigger' name='menuOpen' onclick={toggle_menu} size='l' type='info' />
        )

        const cart_units = $m.cart.cart_units()
        // Favourites live inside the my-portal dropdown; surface pending favourite
        // updates as a notification bubble on the dropdown trigger (so users see them
        // without opening the menu) and on the Favourites item itself once opened
        // (see refresh_favourites_stats).
        const updated_favourites = $s.offers.updated_favourites
        const favourites_badge = updated_favourites > 0 ? (updated_favourites > 99 ? '99+' : updated_favourites) : undefined
        const cart_badge = cart_units > 0 ? (cart_units > 999 ? '999+' : cart_units) : undefined
        // Cart is shown in both the CMS and shop layouts. The cart_bounce hooks bounce it whenever an
        // item is added or removed. On CMS pages it is the way into the shop, which customers missed as
        // a bare cart glyph, so there it carries a label; inside the shop it leads on to checkout.
        const shop_button = (
            <span className='bar-cart-bounce' {...cart_bounce}>
                {is_cms_layout ? (
                    <Button
                        chrome={true}
                        className='bar-shop-button'
                        icon='cart'
                        onclick={() => go_to_offers(shop_route)}
                        text={$t('breadcrumbs.shop')}
                    >
                        {cart_badge !== undefined && <Badge type='brand'>{cart_badge}</Badge>}
                    </Button>
                ) : (
                    <Icon
                        active={route.startsWith(shop_route) && !route.startsWith(favourites_route)}
                        badge={cart_badge}
                        className='btn-action'
                        name='cartRight'
                        onclick={(e) => {
                            e.stopPropagation()
                            go_to_offers(checkout_route)
                        }}
                        size='l'
                        tip={$t('cart.button')}
                        type='info'
                    />
                )}
            </span>
        )

        return (
            <div className='c-shell-header-inner'>
                <Link className='bar-logo' href='/'>
                    {/* The CMS full hero shows the hanging banner plaque; the collapsed hero, shop and
                        mobile swap to the icon-only circle mark. Both always render (stacked) so the
                        swap is a pure CSS crossfade driven by the hero collapse (see cms-layout.css)
                        that survives the cms↔shop layout switch, with SSR/CSS parity. */}
                    <img alt='Logo' className='bar-logo-img bar-logo-full' src='/theme/img/logo-small.png' />
                    <img alt='Logo' className='bar-logo-img bar-logo-icon' src='/theme/img/logo-simple.png' />
                </Link>

                <div className='bar-search'>
                    <SearchSite />
                    {$s.identity.token && (
                        <PricelistDownload
                            className='btn-header portal-shop-pricelist'
                            icon='download'
                            model={$s.offers.export}
                            with_filters={false}
                            with_text={$t('filters.pricelist.download')}
                        />
                    )}
                </div>
                <div className='bar-header'>
                    <div className={classes('action-group', {'is-minimal-shop-chrome': is_cms_layout})}>
                        {/* Both the authenticated nav and the unauthenticated login/register
                            CTAs collapse behind the hamburger on mobile; the drawer adapts
                            its contents to the auth state (see PanelMenu). */}
                        {$s.identity.token ? (
                            <>
                                <div className='portal-shop-desktop-nav'>
                                    {$s.settings.beta && <ThemeSwitcher className='bar-nav-item bar-theme-trigger' />}
                                    <BarLanguageSwitcher />
                                    <Dropdown className='dropdown-my-portal right'>
                                        <div
                                            className={classes('bar-nav-item', {active: route.startsWith('/my-portal')})}
                                            title={$t('my_portal.tip')}
                                        >
                                            <Icon badge={favourites_badge} name='settings' size='l' type='info' />
                                            <Icon name='chevronDown' size='s' type='info' />
                                        </div>
                                        <div className='dropdown-body'>
                                            <Button
                                                active={route.startsWith('/my-portal')}
                                                icon='settings'
                                                link='/my-portal/orders'
                                                text={$t('breadcrumbs.my_portal')}
                                                variant='menu'
                                            />
                                            <Button
                                                active={route.startsWith(favourites_route)}
                                                icon='favourite-o'
                                                link={favourites_route}
                                                text={$t('breadcrumbs.favourites')}
                                                variant='menu'
                                            >
                                                {favourites_badge && (
                                                    <span className='amount-badge'>
                                                        <Badge type='brand'>{favourites_badge}</Badge>
                                                    </span>
                                                )}
                                            </Button>
                                            <Button
                                                icon='logout'
                                                onclick={() => $m.identity.logout()}
                                                text={$t('my_portal.logout')}
                                                variant='menu'
                                            />
                                        </div>
                                    </Dropdown>
                                    {shop_button}
                                </div>
                                {menu_trigger}
                            </>
                        ) : (
                            <>
                                <div className='portal-shop-desktop-nav'>
                                    <Button chrome={true} icon='login' link='/login' text={$t('menu.login')} type='info' />
                                    <Button
                                        chrome={true}
                                        className='mr-3'
                                        icon='profilePlus'
                                        link='/register'
                                        text={$t('registration.signup')}
                                        tip={$t('profile.register')}
                                        type='brand'
                                    />
                                    {$s.settings.beta && <ThemeSwitcher className='bar-nav-item bar-theme-trigger' />}
                                    <BarLanguageSwitcher />
                                    {shop_button}
                                </div>
                                {menu_trigger}
                            </>
                        )}
                    </div>
                </div>
            </div>
        )
    }
}

/** The my-portal embeds keep their own fixed hero photo (they render no CMS page state). */
const MY_PORTAL_HERO_BACKGROUND = 'v5/3e9461e0-801b-4f37-be08-b103372e1a5b'

/** Resolve whether the hero should render collapsed for the current auth state. */
function hero_is_collapsed(hero_mode: string | undefined): boolean {
    return hero_mode === 'collapsed' || (hero_mode === 'collapsed-authenticated' && Boolean($s.identity.token))
}

/**
 * One persistent chrome tree (glass bar + hero band + breadcrumbs) for every portal surface, so
 * switching between the shop and CMS layouts morphs the same elements via CSS transitions (band
 * height, bar width, logo) instead of tearing down and rebuilding the header DOM.
 */
export class ShellHeader extends MithrilComponent {
    view(): m.Children {
        // The committed route, which lags the URL during a navigation and so keeps the chrome on the
        // page still on screen. On the server no route has resolved yet and `m.route.get()` answers with
        // the default '/', which would caption every my-portal/shop request as the home page — there the
        // request's own path is the only truth.
        const route_path = globalThis.__SSR_MODE__ ? $s.env.uri : m.parsePathname(m.route.get()).path
        const is_shop = route_path.startsWith('/shop')
        const is_my_portal = route_path.startsWith('/my-portal')

        // The shop is always the collapsed strip; my-portal embeds show the full hero with their
        // fixed photo; CMS pages follow $s.page, which commit_page swaps atomically with the page
        // content (the preview frame feeds it via postMessage) — so the chrome never runs ahead of
        // a page that is still loading. Collapsed surfaces ignore hero_background: HeroBanner drops
        // the photo and shows the CSS procedural backdrop instead (see content_frame.css).
        const collapsed = is_shop || (!is_my_portal && hero_is_collapsed($s.page.hero_mode))
        const hero_background = is_my_portal ? MY_PORTAL_HERO_BACKGROUND : $s.page.hero_background
        // Hero title + breadcrumbs follow the *rendered* CMS page ($s.page.path lags the URL until
        // the next page commits); the shop and my-portal derive them from the live route instead.
        const path = is_shop || is_my_portal ? undefined : $s.page.path || undefined
        // A path that resolves to no page takes the global 404 copy: its title must be fixed, not
        // derived from the URL that happens to have been typed.
        const copy_kind: PageCopyKind = is_shop || is_my_portal ? 'shell' : $s.page.error === PAGE_NOT_FOUND ? 'not_found' : 'cms'

        // The document title and meta description are the same copy the hero renders, and this is the
        // one component every surface goes through — so publish them from here rather than wiring a
        // hook into each route. It is a no-op unless the copy changed (see apply_document_meta).
        apply_document_meta(copy_kind)

        return (
            <div className='c-shell-header'>
                <HeaderInner />
                <HeroBanner collapsed={collapsed} copy_kind={copy_kind} hero_background={hero_background} path={path} />
                <Breadcrumbs copy_kind={copy_kind} path={path} />
            </div>
        )
    }
}
